import confetti from "canvas-confetti";
import _, { capitalize } from "lodash";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import IconArrowLeft from "../components/Icon/IconArrowLeft";
import IconBox from "../components/Icon/IconBox";
import IconCaretDown from "../components/Icon/IconCaretDown";
import IconChartSquare from "../components/Icon/IconChartSquare";
import IconChecks from "../components/Icon/IconChecks";
import IconCircleCheck from "../components/Icon/IconCircleCheck";
import IconClipboardText from "../components/Icon/IconClipboardText";
import IconCoffee from "../components/Icon/IconCoffee";
import IconCreditCard from "../components/Icon/IconCreditCard";
import IconDollarSignCircle from "../components/Icon/IconDollarSignCircle";
import IconHeart from "../components/Icon/IconHeart";
import IconHome from "../components/Icon/IconHome";
import IconInfoCircle from "../components/Icon/IconInfoCircle";
import IconListCheck from "../components/Icon/IconListCheck";
import IconMapPin from "../components/Icon/IconMapPin";
import IconNotes from "../components/Icon/IconNotes";
import IconShoppingCart from "../components/Icon/IconShoppingCart";
import IconSun from "../components/Icon/IconSun";
import IconTag from "../components/Icon/IconTag";
import IconTrendingUp from "../components/Icon/IconTrendingUp";
import IconVideo from "../components/Icon/IconVideo";
import IconWheel from "../components/Icon/IconWheel";
import { useUserContext } from "../contexts/UserContext";
import logger from '../utils/logger';
import { categoriesBySectionInGroups } from "../global/categories";
import { CurrencyData, OptionalTransactionType, SelectOption, TransactionType } from "../global/types";
import useCurrency from "../hooks/useCurrency";
import { applyWizardSetup, updateUserLanguage } from "../services/usersServices";
import { cn } from "../utils/misc";
import { showErrorNotification, showSuccessNotification } from "../utils/notificationUtils";
import { IconArrowRight } from "./Icon/IconArrowRight";
import IconGlobe from "./Icon/IconGlobe";
import ReactSelect from "./ReactSelect";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useSelector } from "react-redux";
import { IRootState } from "../store";
import Dropdown from "./Dropdown";
import { LANGUAGE_OPTIONS } from "../global/languages";
import { CALCULATION_CURRENCY } from "../utils/currencyUtils";
import { analytics } from "@/utils/analytics";
import { SUPPORTED_CURRENCIES } from "@sentola/money";

type AmountEntry = {
    name: string;
    amount?: number;
}

export type SetupWizardInformations = {
    goals: string[];
    method: string;
    region: RegionInputs;
    income: IncomeInputs;
    expenses: ExpenseInputs;
}

enum WizardState { FORM, APPLYING, DONE }

function SetupWizard({ close }: {
    close: () => void
}) {
    const { t } = useTranslation();
    const { userId } = useUserContext();
    const [state, setState] = useState<WizardState>(WizardState.FORM);
    const [prevFormState, setPrevFormState] = useState<SetupWizardInformations | undefined>(undefined);

    useEffect(() => {
        analytics.track('onboarding_started');
    }, []);

    const handleSubmit = (infos: SetupWizardInformations) => {
        if (!userId) return;
        logger.debug('Setup complete with infos:', infos);
        setState(WizardState.APPLYING);
        setPrevFormState(infos);
        applyWizardSetup(infos, userId)
            .then(() => {
                analytics.track('onboarding_completed');
                showSuccessNotification(t('sw_page.setup_complete'));
                setState(WizardState.DONE);
                setTimeout(() => {
                    close();
                }, 1000);
            })
            .catch((error) => {
                setState(WizardState.FORM);
                console.error("Failed to apply setup:", error);
                showErrorNotification(t('sw_page.setup_failed'));
            })
    }

    return (
        <div className={cn("animate-fade-in relative z-[51]", state === WizardState.DONE && "animate-fade-out animate-duration-fast")}>
            <div className="bg-[#fafafa] fixed inset-0 z-[51] overflow-y-auto">
                {state === WizardState.FORM && <Setup onComplete={handleSubmit} defaultFormState={prevFormState} />}
                {
                    state === WizardState.APPLYING && <div className="animate-fade-in-up min-h-screen flex flex-col justify-center items-center">
                        <span className="flex text-xl text-dark font-bold">
                            {t('sw_page.applying_configuration')}
                        </span>
                        <span className="animate-spin-clockwise animate-iteration-count-infinite border-8 border-primary border-l-transparent rounded-full size-12 mt-10"></span>
                    </div>
                }
                {
                    state === WizardState.DONE && <div className="min-h-screen flex flex-col justify-center items-center">
                        <span className="flex text-xl text-dark font-bold">
                            {t('sw_page.setup_complete_redirecting')}
                        </span>
                        <IconChecks className="text-primary size-12 mt-10" />
                    </div>
                }
            </div>
        </div>
    )
}

function Setup({ onComplete, defaultFormState }: {
    onComplete: (infos: SetupWizardInformations) => void,
    defaultFormState: SetupWizardInformations | undefined
}) {
    const { t } = useTranslation();
    const { STEPS, EXPENSES } = useSetupWizardData();
    const [step, setStep] = useState(0);
    const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
    const [selectedMethod, setSelectedMethod] = useState<string>();
    const [regionPayload, setRegionPayload] = useState<RegionInputs>({ currency: undefined, region: 'other' });
    const [incomePayload, setIncomePayload] = useState<IncomeInputs>({ amount: undefined, others: [] });
    const [expensePayload, setExpensePayload] = useState<ExpenseInputs>({
        mains: EXPENSES.map(exp => ({ name: exp.name, amount: undefined, label: exp.label })),
        others: []
    });

    const complete = () => {
        onComplete(
            {
                goals: selectedGoals,
                method: selectedMethod || '',
                region: regionPayload,
                income: incomePayload,
                expenses: expensePayload
            }
        );
    };

    const goToNextStep = () => {
        if (step === STEPS.length - 1)
            return complete();
        setStep(step + 1);
        analytics.track('onboarding_step_completed', { step_index: step + 1, step_name: STEPS[step].name });
    };

    const isCurrentStepComplete = () => {
        if (step === 0) return selectedGoals.length > 0;
        else if (step === 1) return !!selectedMethod;
        else if (step === 2) return !!regionPayload.currency && !!regionPayload.region;
        else if (step === 3 || step === 4) return true;
        else if (step === 5) return true;
    }

    useEffect(() => {
        if (defaultFormState) {
            setSelectedGoals(defaultFormState.goals);
            setSelectedMethod(defaultFormState.method);
            setRegionPayload(defaultFormState.region);
            setIncomePayload(defaultFormState.income);
            setExpensePayload(defaultFormState.expenses);
            setStep(5);
        }
    }, [defaultFormState]);

    const ghostBtnClass = [
        "flex items-center gap-4 text-sm",
        "font-medium text-sm text-center",
        "rounded-lg px-4 py-3 hover:opacity-100 hover:bg-black-light transition-all",
    ];
    const isNextDisabled = !isCurrentStepComplete();
    const isSkippableStep = (step === 3 || step === 4);

    return (
        <div className="min-h-screen py-8 px-4 text-dark">
            <div className="mx-auto max-w-2xl">
                <StepsNavigator currentStep={step} />
                <div className="flex flex-col p-8 bg-white border shadow-xl rounded-3xl">
                    <div>
                        <div className="mb-8 text-center flex flex-col items-center gap-2">
                            <IconWrapper Icon={STEPS[step].icon} />
                            <h2 className="font-bold text-2xl mt-2"> {STEPS[step].title} </h2>
                            <div className=""> {STEPS[step].subtitle} </div>
                        </div>

                        <div className="*:animate-fade-in-down ">
                            {step === 0 && <Step1 selectedGoals={selectedGoals} setSelectedGoals={setSelectedGoals} />}
                            {step === 1 && <Step2 selectedMethod={selectedMethod} setSelectedMethod={setSelectedMethod} />}
                            {step === 2 && <Step3 regionPayload={regionPayload} setRegionPayload={setRegionPayload} />}
                            {
                                step === 3 && <Step4
                                    currency={regionPayload.currency!}
                                    incomePayload={incomePayload}
                                    setIncomePayload={setIncomePayload}
                                />
                            }
                            {
                                step === 4 && <Step5
                                    currency={regionPayload.currency!}
                                    expensePayload={expensePayload}
                                    setExpensePayload={setExpensePayload}
                                />
                            }
                            {
                                step === 5 && <Step6
                                    infos={{
                                        goals: selectedGoals,
                                        method: selectedMethod || '',
                                        region: regionPayload,
                                        income: incomePayload,
                                        expenses: expensePayload
                                    }}
                                    celebrate={defaultFormState === undefined}
                                />
                            }
                        </div>
                    </div>

                    <div className="mt-8 space-y-4 text-center">
                        {
                            isSkippableStep && <button
                                type="button"
                                onClick={goToNextStep}
                                className={cn([
                                    ...ghostBtnClass,
                                    "sm:hidden w-full",
                                ])}
                            >
                                <span className="flex items-center mx-auto gap-2">
                                    {t('skip_for_now')}
                                </span>
                            </button>
                        }
                        <div className="flex justify-between items-center gap-4">
                            <div className="flex-1" >
                                <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} className={cn(
                                    ...ghostBtnClass,
                                    step === 0 && 'opacity-0 pointer-events-none'
                                )}>
                                    <IconArrowRight />
                                    <span>{t('back')}</span>
                                </button>
                            </div>
                            {
                                isSkippableStep && <button
                                    type="button"
                                    onClick={goToNextStep}
                                    className={cn([...ghostBtnClass, "hidden sm:block"])}
                                >
                                    {t('skip_for_now')}
                                </button>
                            }
                            <button type="button" onClick={goToNextStep}
                                className={cn(
                                    "text-white bg-gradient-to-r from-primary to-[#2a5563] hover:bg-gradient-to-bl",
                                    "font-medium text-sm text-center",
                                    "rounded-lg",
                                    "px-8 py-3",
                                    "flex gap-2 items-center",
                                    isNextDisabled && "opacity-50 cursor-not-allowed"
                                )}
                                disabled={isNextDisabled}
                            >
                                {
                                    step === STEPS.length - 1
                                        ? <>
                                            {t('sw_page.complete_setup')}
                                            <IconChecks />
                                        </>
                                        : <>
                                            {t('continue')}
                                            <IconArrowLeft />
                                        </>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const sizeMap: Record<number, { w: string, c: string }> = {
    6: { w: "size-6", c: "size-4" },
    8: { w: "size-8", c: "size-4" },
    10: { w: "size-10", c: "size-5" },
    12: { w: "size-12", c: "size-6" },
};
function IconWrapper({ Icon, color, size = 12 }: {
    Icon: React.ComponentType<{ className?: string }>,
    color?: string,
    size?: 6 | 8 | 10 | 12
}) {
    return (
        <div
            className={cn(
                "rounded-lg flex justify-center items-center aspect-square",
                color ? `bg-${color}` : "bg-secondary",
                "bg-secondary",
                sizeMap[size].w,
            )}
        >
            <Icon className={cn("text-white", sizeMap[size].c)} />
        </div>
    );
}

function StepsNavigator({ currentStep }: {
    currentStep: number
}) {
    const { STEPS } = useSetupWizardData();
    const perc = (((currentStep + 1) / STEPS.length) * 100).toFixed(0);
    const { t } = useTranslation();
    const isRtl = useSelector((state: IRootState) => state.themeConfig.rtlClass) === 'rtl' ? true : false;
    const themeConfig = useSelector((state: IRootState) => state.themeConfig);
    const { user } = useUserContext();
    const [flag, setFlag] = useState(themeConfig.locale);

    return (
        <div className="mb-8">
            <div className="grid grid-cols-3 items-center mb-4 text-sm">
                <p className="opacity-80 font-medium">
                    {t("sw_page.step_x_of_y", { current: String(currentStep + 1), total: String(STEPS.length) })}
                </p>
                <div className="dropdown shrink-0 mx-auto">
                    <Dropdown
                        offset={[0, 8]}
                        placement={`${isRtl ? 'bottom-start' : 'bottom-end'}`}
                        btnClassName="block p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60"
                        button={<img className="w-5 h-5 object-cover rounded-full" src={`/assets/images/flags/${flag.toUpperCase()}.svg`} alt="flag" />}
                    >
                        <ul className="!px-2 text-dark dark:text-white-dark grid grid-cols-2 gap-2 font-semibold dark:text-white-light/90 w-[280px]">
                            {LANGUAGE_OPTIONS.map((item) => {
                                return (
                                    <li key={item.code}>
                                        <button
                                            type="button"
                                            className={`flex w-full hover:text-primary dark:hover:text-primary-light rounded-lg ${i18next.language === item.code ? 'bg-primary/10 text-primary dark:bg-primary-light/10 dark:text-primary-light' : ''}`}
                                            onClick={async () => {
                                                i18next.changeLanguage(item.code);
                                                updateUserLanguage(user?.id, item.code);
                                                setFlag(item.code);
                                            }}
                                        >
                                            <img src={`/assets/images/flags/${item.code.toUpperCase()}.svg`} alt="flag" className="w-5 h-5 object-cover rounded-full" />
                                            <span className="ltr:ml-3 rtl:mr-3">{item.name}</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </Dropdown>
                </div>
                <p className="opacity-60 text-right">{t('sw_page.sentola_setup')}</p>
            </div>
            <div className="w-full bg-dark/20 rounded-full h-2">
                <div
                    className="bg-dark h-2 rounded-full transition-all duration-700 ease-out progress-pulse"
                    style={{ width: `${perc}%` }}
                />
            </div>
            <div className="flex justify-between items-center mt-3">
                {
                    Array.from({ length: STEPS.length }).map((_, index) => {
                        const active = currentStep >= index;

                        return (
                            <div className="flex flex-col items-center" key={index}>
                                <div className={cn(
                                    "flex justify-center items-center size-8 rounded-full bg-dark text-white",
                                    !active && "bg-dark/50 text-white/50 font-medium"
                                )}>
                                    {index + 1}
                                </div>
                                <div className={cn("mt-1", active ? "text-dark" : "text-dark/50")}>
                                    {STEPS[index].name as string}
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        </div >
    );
}

function Step1({ selectedGoals, setSelectedGoals }: {
    selectedGoals: string[],
    setSelectedGoals: (goals: string[]) => void
}) {
    const { GOALS } = useSetupWizardData();
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            {GOALS.map((goal, index) => {
                const isSelected = selectedGoals.includes(goal.name);

                const toggleGoal = () => {
                    if (isSelected) {
                        setSelectedGoals(selectedGoals.filter(g => g !== goal.name));
                    } else {
                        setSelectedGoals([...selectedGoals, goal.name]);
                    }
                };

                return (
                    <div
                        key={index}
                        onClick={toggleGoal}
                        className={cn(
                            "panel flex justify-between p-4 gap-4 border rounded-lg cursor-pointer bg-slate-50 shadow-none hover:shadow-md transition-shadow",
                            isSelected && "bg-primary/10 border-primary sm:bg-slate-50",
                        )}
                    >
                        <IconWrapper Icon={goal.icon} />
                        <div className="flex items-center w-full">
                            <div className="flex-1">
                                <div className="font-bold">{goal.name}</div>
                                <div className="text-sm">{goal.text}</div>
                            </div>
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={toggleGoal}
                                className="mx-4 form-checkbox size-6 hidden sm:block"
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    )
}

function Step2({ selectedMethod, setSelectedMethod }: {
    selectedMethod: string | undefined,
    setSelectedMethod: (method: string) => void
}) {
    const { METHODS } = useSetupWizardData();
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            {
                METHODS.map((method, index) => {
                    const toggle = () => {
                        setSelectedMethod(method.name);
                    }
                    const isSelected = selectedMethod === method.name;

                    return (
                        <button
                            key={index}
                            className={cn(
                                "panel w-full text-left flex items-start justify-start p-6 gap-4 border rounded-lg cursor-pointer bg-slate-50 shadow-none hover:shadow-md transition-shadow",
                                isSelected && "bg-primary/10 border-primary sm:bg-slate-50",
                            )}
                            onClick={toggle}
                        >
                            <input
                                type="radio" name="budgeting-method" checked={selectedMethod === method.name} onChange={toggle}
                                className="hidden sm:block"
                            />
                            <IconWrapper Icon={method.icon} />
                            <div className="flex flex-col gap-1">
                                <div className="font-bold"> {method.name} </div>
                                <div className="text-sm mb-2"> {method.text} </div>
                                <small className="text-[12px] text-info bg-info-light w-fit px-2 rounded-full"> {method.note} </small>
                            </div>
                        </button>
                    )
                })
            }
        </div>
    )
}


function LocationIcon({ className }: { className?: string }) {
    return (
        <span className="animate-pulse size-6 mx-auto inline-block rounded-full bg-white"></span>
    )
}
type RegionInputs = {
    currency?: CurrencyData,
    region?: string
}
function Step3({ regionPayload, setRegionPayload }: {
    regionPayload: RegionInputs,
    setRegionPayload: Dispatch<SetStateAction<RegionInputs>>
}) {
    const { t } = useTranslation();
    const [locating, setLocating] = useState(false);
    // const regions = ["United States", "United Kingdom", "Russia", "Turkey", "Uganda", "Canada", "Other"];
    // const regionCodes: string[] = ["us", "gb", "ru", "tr", "ug", "ca"];
    const regionOptions: SelectOption[] = [
        { value: 'us', label: t('country.us') },
        { value: 'gb', label: t('country.gb') },
        { value: 'ru', label: t('country.ru') },
        { value: 'tr', label: t('country.tr') },
        { value: 'ug', label: t('country.ug') },
        { value: 'ca', label: t('country.ca') },
    ];

    const { currencies: allCurrencies } = useCurrency();
    const currencies = useMemo(
        () => allCurrencies.filter(c => (SUPPORTED_CURRENCIES as readonly string[]).includes(c.code)),
        [allCurrencies]
    );
    const currencyOptions: { value: string; label: string }[] = useMemo(() => {
        return currencies.map(currency => ({
            value: currency.code,
            label: `${_.upperFirst(currency.name)} (${currency.symbol})`
        }));
    }, [currencies]);


    const detectLocation = () => {
        if (!navigator.geolocation) {
            alert(t('sw_page.geolocation_not_supported'));
            return;
        }

        setLocating(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`);
                const data = await res.json();
                const country = data.address.country;
                const countryCode = data.address.country_code.toUpperCase();

                const res2 = await fetch(`https://restcountries.com/v3.1/alpha/${countryCode}`);
                const data2 = await res2.json();
                const currencyCode = Object.keys(data2[0].currencies)[0] || CALCULATION_CURRENCY;

                const currency = currencies.find(c => c.code === countryCode) || currencies.find(c => c.code === currencyCode);
                const region = _.find(regionOptions, { value: countryCode.toLowerCase() }) ? countryCode : "other";

                setRegionPayload(prev => ({ ...prev, currency, region }));
            } catch (error) {
                alert(t('sw_page.failed_location_detection'));
            } finally {
                setLocating(false);
            }
        }, () => {
            setLocating(false);
            alert(t('sw_page.unable_to_retrieve_your_location'));
        });
    }

    return (
        <div className="flex flex-col gap-6">
            <div>
                <label htmlFor="currency">{t('currency')}</label>
                <ReactSelect
                    options={currencyOptions.concat([{ value: 'other', label: t('other') }])}
                    onChange={
                        (option) => setRegionPayload(prev => ({
                            ...prev,
                            currency: currencies.find(c => c.code === (option?.value || "USD"))
                        }))
                    }
                    value={currencyOptions.find(c => c.value === regionPayload.currency?.code)}
                    id="confCurrency"
                />
            </div>
            <div>
                <label htmlFor="region">{t('region')}</label>
                <ReactSelect
                    options={regionOptions.concat({
                        value: 'other',
                        label: t('other')
                    })}
                    onChange={
                        (option) => setRegionPayload(prev => ({
                            ...prev,
                            region: option!.value
                        }))
                    }
                    value={{
                        value: regionPayload.region,
                        label: regionPayload.region!.toLowerCase() === "other" ? t("other") : t(`country.${regionPayload.region}` as any) || regionPayload.region
                    }}
                    id="region"
                    name="region"
                />
            </div>
            <div className={cn(
                "panel flex items-start justify-start p-4 gap-4 border rounded-lg mb-4 cursor-pointer bg-slate-50 shadow-none hover:shadow-md transition-all",
                locating && "opacity-50 pointer-events-none"
            )}>
                <IconWrapper Icon={
                    locating
                        ? LocationIcon
                        : IconMapPin
                } />
                <div className="flex flex-col gap-1">
                    <div className="font-bold"> {t('sw_page.auto_detected_location')} </div>
                    <p className="text-sm mb-2">{t('sw_page.auto_detected_location_description')}</p>
                    <button type="button" onClick={detectLocation} className="font-bold text-sm text-left text-secondary transition-all hover:underline hover:text-orange-600">{t('sw_page.auto_detect_my_location')}</button>
                </div>
            </div>
        </div>
    )
}

type IncomeInputs = {
    amount?: number,
    others: AmountEntry[]
};
function Step4({ currency, incomePayload, setIncomePayload }: {
    currency: CurrencyData,
    incomePayload: IncomeInputs,
    setIncomePayload: Dispatch<SetStateAction<IncomeInputs>>
}) {
    const { t } = useTranslation();
    const [openMultiple, setOpenMultiple] = useState(false);
    const newOthers = [...incomePayload.others];
    const handleOtherSourceChange = (index: number, field: 'name' | 'amount', val: string | number) => {
        const value = (field === 'amount') ? Number(val) : val;
        (newOthers[index][field] as string | number) = value;
        setIncomePayload(prev => ({ ...prev, others: newOthers }));
    };

    const sections = categoriesBySectionInGroups[TransactionType.INCOME] || [];
    const groupedOptions = Object.entries(sections).map(([section, subcategories]) => ({
        label: section,
        options: _.differenceBy(subcategories, [{ name: "Base Salary" }, ...incomePayload.others], 'name')
            .map(subcategory => ({
                value: subcategory.name,
                label: subcategory.name,
                color: subcategory.color,
                type: subcategory.type
            }))
    }));

    return (
        <div>
            <div className="mb-6">
                <label htmlFor="monthlyIncome">{t('sw_page.monthly_take_home_income')}</label>
                <div>
                    <div className="flex">
                        <div className="bg-[#eee] flex justify-center items-center rounded-l-md px-3 font-semibold border border-r-0 border-white-light dark:border-[#17263c] dark:bg-[#1b2e4b]">
                            {currency.symbol}
                        </div>
                        <input className="rounded-l-none form-input" type="number" name="amount" value={incomePayload.amount} onChange={() => setIncomePayload(prev => ({ ...prev, amount: Number((event?.target as HTMLInputElement).value) }))} />
                    </div>
                    <small>{t('sw_page.enter_your_after_tax_income')}</small>
                </div>
            </div>

            <div className={cn(
                "panel flex items-start justify-start p-4 gap-4 border rounded-lg mb-4 cursor-pointer bg-slate-50 shadow-none hover:shadow-md transition-all",
            )}>
                <IconWrapper Icon={IconInfoCircle} color="warning" />
                <div>
                    <div className="font-bold"> {t('pro_tip')} </div>
                    <p className="text-sm">{t('sw_page.step_4_pro_tip')}</p>
                </div>
            </div>

            <div className="panel shadow-none border bg-slate-50 p-4 rounded-lg">
                <button type="button" onClick={() => setOpenMultiple(!openMultiple)} className="w-full flex items-center gap-4 text-left font-bold text-dark">
                    {t('sw_page.add_multiple_income')}
                    <div className={cn(
                        "ml-auto transition-transform",
                        openMultiple && "rotate-180"
                    )}>
                        <IconCaretDown />
                    </div>
                </button>
                <AnimateHeight duration={300} height={openMultiple ? 'auto' : 0}>
                    <div className="flex flex-col gap-10 sm:gap-4 mt-4">
                        {
                            incomePayload.others.map((income, index) => (
                                <div key={index} className="flex flex-col sm:flex-row gap-4">
                                    <ReactSelect
                                        options={groupedOptions}
                                        placeholder={t('select_income_placeholder')}
                                        className="flex-1"
                                        onChange={(option: any) => {
                                            handleOtherSourceChange(index, 'name', option.value)
                                        }}
                                        value={{
                                            value: incomePayload.others[index].name,
                                            label: incomePayload.others[index].name,
                                        }}
                                    />
                                    <div className="flex flex-1">
                                        <div className="bg-[#eee] flex justify-center items-center rounded-l-md px-3 font-semibold border border-r-0 border-white-light dark:border-[#17263c] dark:bg-[#1b2e4b]">
                                            {currency.symbol}
                                        </div>
                                        <input className="form-input rounded-l-none" type="number" step="any" placeholder={t('amount')} value={income.amount} onChange={(event) => handleOtherSourceChange(index, 'amount', event.target.value)} />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            newOthers.splice(index, 1);
                                            setIncomePayload(prev => ({ ...prev, others: newOthers }));
                                        }}
                                        className="btn btn-dark shadow-none"
                                    >{t('remove')}</button>
                                </div>
                            ))
                        }
                    </div>
                    <button type="button"
                        onClick={() => setIncomePayload(prev => ({ ...prev, others: [...prev.others, { name: '', amount: undefined }] }))}
                        className="mt-4 text-sm text-secondary font-bold hover:underline"
                    >
                        {t('sw_page.add_another_source')}
                    </button>
                </AnimateHeight>
            </div>
        </div>
    );
}

type ExpenseInputs = {
    mains: (AmountEntry & { label: string })[],
    others: AmountEntry[]
};
function Step5({ currency, expensePayload, setExpensePayload }: {
    currency: CurrencyData,
    expensePayload: ExpenseInputs,
    setExpensePayload: Dispatch<SetStateAction<ExpenseInputs>>
}) {
    const { t } = useTranslation();
    const { EXPENSES } = useSetupWizardData();
    const [openMultiple, setOpenMultiple] = useState(false);
    const handleExpenseChange = (index: number, field: 'name' | 'amount', group: 'mains' | 'others', val: string | number) => {
        const value = (field === 'amount') ? Number(val) : val;
        const newGroup = [...expensePayload[group]];
        (newGroup[index][field] as string | number) = value;
        setExpensePayload(prev => ({ ...prev, [group]: newGroup }));
    };

    const sections = categoriesBySectionInGroups[OptionalTransactionType.EXPENSES] || [];
    const groupedOptions = Object.entries(sections).map(([section, subcategories]) => ({
        label: section,
        options: _.differenceBy(subcategories, [...expensePayload.others, ...expensePayload.mains], 'name')
            .map(subcategory => ({
                value: subcategory.name,
                label: subcategory.name,
                color: subcategory.color,
                type: subcategory.type
            }))
    }));

    return (
        <div className="flex flex-col gap-6">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {
                    EXPENSES.map((expense, index) => (
                        <div key={index} className="panel flex flex-col justify-between shadow-none border bg-slate-50 p-4 rounded-lg hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-2 mb-2">
                                <IconWrapper Icon={expense.icon} size={6} />
                                <span className="font-bold text-sm"> {expense.label} </span>
                            </div>
                            <div className="flex">
                                <div className="bg-[#eee] flex justify-center items-center rounded-l-md px-3 font-semibold border border-r-0 border-white-light dark:border-[#17263c] dark:bg-[#1b2e4b]">
                                    {currency.symbol}
                                </div>
                                <input
                                    type="number"
                                    step="any"
                                    className="form-input rounded-l-none"
                                    value={expensePayload.mains[index]?.amount}
                                    onChange={(event) => handleExpenseChange(index, 'amount', 'mains', event.target.value)}
                                />
                            </div>
                        </div>
                    ))
                }
            </div>
            <div className="panel shadow-none border bg-slate-50 p-4 rounded-lg">
                <button type="button" onClick={() => setOpenMultiple(!openMultiple)} className="w-full flex items-center gap-4 text-left font-bold text-dark">
                    {t('sw_page.add_custom_expense')}
                    <div className={cn(
                        "ml-auto transition-transform",
                        openMultiple && "rotate-180"
                    )}>
                        <IconCaretDown />
                    </div>
                </button>
                <AnimateHeight duration={300} height={openMultiple ? 'auto' : 0}>
                    <div className="flex flex-col gap-10 sm:gap-4 mt-4">
                        {
                            expensePayload.others.map((expense, index) => (
                                <div key={index} className="flex flex-col sm:flex-row gap-4">
                                    <ReactSelect
                                        options={groupedOptions}
                                        placeholder={t('select_expense_placeholder')}
                                        className="flex-1"
                                        onChange={(option: any) => {
                                            handleExpenseChange(index, 'name', 'others', option.value)
                                        }}
                                        value={{
                                            value: expensePayload.others[index].name,
                                            label: expensePayload.others[index].name,
                                        }}
                                    />

                                    <div className="flex flex-1">
                                        <div className="bg-[#eee] flex justify-center items-center rounded-l-md px-3 font-semibold border border-r-0 border-white-light dark:border-[#17263c] dark:bg-[#1b2e4b]">
                                            {currency.symbol}
                                        </div>
                                        <input className="form-input rounded-l-none" type="number" step="any" placeholder={t('amount')} value={expense.amount} onChange={(event) => handleExpenseChange(index, 'amount', 'others', event.target.value)} />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newOthers = [...expensePayload.others];
                                            newOthers.splice(index, 1);
                                            setExpensePayload(prev => ({ ...prev, others: newOthers }));
                                        }}
                                        className="btn btn-dark shadow-none"
                                    >{t('remove')}</button>
                                </div>
                            ))
                        }
                    </div>
                    <button type="button"
                        onClick={() => setExpensePayload(prev => ({ ...prev, others: [...prev.others, { name: '', amount: undefined }] }))}
                        className="mt-4 text-sm text-secondary font-bold hover:underline"
                    >
                        {t('sw_page.add_another_expense')}
                    </button>
                </AnimateHeight>
            </div>
            <div className="panel shadow-none border bg-secondary text-white flex justify-between items-center font-bold text-lg">
                <span>
                    {t('total_monthly_expenses')}
                </span>
                <span className="font-bold text-xl">
                    {currency.formatMoney([...expensePayload.mains, ...expensePayload.others].reduce((sum, exp) => sum + (exp.amount || 0), 0))}
                </span>
            </div>
        </div>
    );
}

function CongratSection({ celebrate = true }: { celebrate: boolean }) {
    const { t } = useTranslation();
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!divRef.current || !celebrate) return;

        const rect = divRef.current.getBoundingClientRect();

        confetti({
            particleCount: 150,
            spread: 90,
            startVelocity: 40,
            origin: {
                x: (rect.left + rect.width / 2) / window.innerWidth,
                y: (rect.top + rect.height / 2) / window.innerHeight,
            },
        });
    }, [celebrate]);

    return (
        <div
            ref={divRef}
            className="bg-gradient-to-br from-[#f272a3] to-[#f2c172] rounded-lg"
        >
            <div className="flex flex-col gap-4 text-center items-center p-8 m-1 bg-white rounded-lg">
                <div className="font-bold">
                    <span className="bg-gradient-to-br from-[#f272a3] to-[#f2c172] text-transparent bg-clip-text text-lg flex gap-1">
                        <span>✨</span>
                        {t('sw_page.congratulations_title')}
                        <span>✨</span>
                    </span>
                </div>
                <p>{t('sw_page.congratulations_subtitle')}</p>
            </div>
        </div>

    );
}

function Step6({
    infos: { goals: _goals, method, region, income, expenses },
    celebrate
}: {
    infos: SetupWizardInformations,
    celebrate: boolean
}) {
    const { STEPS, GOALS } = useSetupWizardData();
    const { t } = useTranslation();
    const goals = _goals.map(g => GOALS.find(goal => goal.name === g));
    const totalIncome = (income.amount || 0) + income.others.reduce((sum, inc) => sum + (inc.amount || 0), 0);
    const totalExpense = expenses.mains.reduce((sum, exp) => sum + (exp.amount || 0), 0) + expenses.others.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    const available = totalIncome - totalExpense;
    const savingsRate = totalIncome > 0 ? (available / totalIncome) * 100 : 0;

    const formatMoney = region.currency?.formatMoney || ((val: number) => `$${val}`);
    const showFinancialOverview = totalExpense > 0 || totalIncome > 0;

    return (
        <div className="flex flex-col gap-6">
            <CongratSection celebrate={celebrate} />
            <div className="grid sm:grid-cols-2 gap-4">
                <Step6Item Icon={STEPS[0].icon} title={t('financial_goals')} className="sm:col-span-2">
                    <div className="flex gap-2 flex-wrap">
                        {
                            goals.map((goal, index) => (
                                <div key={index} className="flex items-center gap-2 bg-white border px-4 py-2 w-fit rounded-full">
                                    <IconWrapper Icon={goal?.icon!} size={6} />
                                    <span className="font-bold text-sm">{goal?.name}</span>
                                </div>
                            ))
                        }
                    </div>
                </Step6Item>

                <Step6Item Icon={STEPS[1].icon} title={t('sw_page.budget_method')}>
                    <p className="font-medium"> {method} </p>
                </Step6Item>

                <Step6Item Icon={STEPS[2].icon} title={t('sw_page.location_and_currency')}>
                    <p className="font-medium">
                        {region.region || t('sw_page.region_not_set')} • {
                            region.currency
                                ? `${capitalize(region.currency.name)} (${region.currency.code})`
                                : t('sw_page.currency_not_set')
                        }
                    </p>
                </Step6Item>

                {
                    showFinancialOverview && (
                        <Step6Item Icon={STEPS[3].icon} title={t('financial_overview')}>
                            <div className="grid grid-cols-2 gap-4">
                                {
                                    totalIncome > 0 && <div>
                                        <p className="text-sm">{capitalize(t('monthly_income'))}</p>
                                        <p className="font-bold">{formatMoney(totalIncome)}</p>
                                    </div>
                                }
                                {
                                    totalExpense > 0 && <div>
                                        <p className="text-sm">{capitalize(t('monthly_expenses'))}</p>
                                        <p className="font-bold">{formatMoney(totalExpense)}</p>
                                    </div>
                                }
                                {
                                    available > 0 && <div>
                                        <p className="text-sm">{t('sw_page.available_for_goals')}</p>
                                        <p className="font-bold">{formatMoney(totalIncome - totalExpense)}</p>
                                    </div>
                                }
                                {
                                    available > 0 && <div>
                                        <p className="text-sm">{capitalize(t('savings_rate'))}</p>
                                        <p className="font-bold">{savingsRate.toFixed(1)} %</p>
                                    </div>
                                }
                            </div>
                        </Step6Item>
                    )
                }

                <Step6Item Icon={STEPS[4].icon} title={t('sw_page.whats_next')} className={cn(!showFinancialOverview && "sm:col-span-2")}>
                    <ul >
                        <li className="flex items-center gap-2 mb-2">
                            <IconCircleCheck className="text-secondary w-5" />
                            {t('sw_page.setup_your_bank_account')}
                        </li>
                        <li className="flex items-center gap-2 mb-2">
                            <IconCircleCheck className="text-secondary w-5" />
                            {t('sw_page.setup_your_first_budget')}
                        </li>
                        <li className="flex items-center gap-2 mb-2">
                            <IconCircleCheck className="text-secondary w-5" />
                            {t('sw_page.create_action_plans')}
                        </li>
                    </ul>
                </Step6Item>

            </div>
        </div>
    );
}

function Step6Item({ Icon, title, color, className, children }: {
    Icon: React.ComponentType<{ className?: string }>,
    title: string,
    color?: string,
    className?: string,
    children: React.ReactNode
}) {
    return (
        <div className={cn("panel bg-slate-50 border p-4 rounded-lg", className)}>
            <div className="flex items-center gap-4">
                <IconWrapper Icon={Icon} size={8} />
                <span className="font-bold">{title}</span>
            </div>
            {
                children && (
                    <div className="flex flex-col gap-2 mt-4">
                        {children}
                    </div>
                )
            }
        </div>
    );
}

function useSetupWizardData() {
    const { t } = useTranslation();

    const STEPS = [
        {
            icon: IconListCheck,
            name: t('goals'),
            title: t('sw_page.goal.title'),
            subtitle: t('sw_page.goal.subtitle'),
        },
        {
            icon: IconChartSquare,
            name: t('budget'),
            title: t('sw_page.budget.title'),
            subtitle: t('sw_page.budget.subtitle'),
        },
        {
            icon: IconGlobe,
            name: t('region'),
            title: t('sw_page.region.title'),
            subtitle: t('sw_page.region.subtitle'),
        },
        {
            icon: IconDollarSignCircle,
            name: t('income'),
            title: t('sw_page.income.title'),
            subtitle: t('sw_page.income.subtitle')
        },
        {
            icon: IconShoppingCart,
            name: t('expenses'),
            title: t('sw_page.expenses.title'),
            subtitle: t('sw_page.expenses.subtitle')
        },
        {
            icon: IconChecks,
            name: t('summary'),
            title: t('sw_page.summary.title'),
            subtitle: t('sw_page.summary.subtitle')
        },
    ];

    const GOALS = [
        {
            icon: IconHeart,
            name: t("sw_page.goal_build_emergency_fund"),
            text: t("sw_page.goal_build_emergency_fund_text"),
        },
        {
            icon: IconCreditCard,
            name: t("sw_page.goal_pay_off_debt"),
            text: t("sw_page.goal_pay_off_debt_text"),
        },
        {
            icon: IconTrendingUp,
            name: t("sw_page.goal_start_investing"),
            text: t("sw_page.goal_start_investing_text"),
        },
        {
            icon: IconHome,
            name: t("sw_page.goal_buy_home"),
            text: t("sw_page.goal_buy_home_text"),
        },
        {
            icon: IconSun,
            name: t("sw_page.goal_plan_retirement"),
            text: t("sw_page.goal_plan_retirement_text"),
        },
    ];

    const METHODS = [
        {
            name: t('50_30_20_rule'),
            text: t('50_30_20_rule_long'),
            note: t('sw_page.great_for_beginners'),
            icon: IconChartSquare,
        },
        {
            name: t('sw_page.zero_based_budget'),
            text: t('sw_page.every_dollar_has_a_purpose'),
            note: t('sw_page.more_detailed_tracking'),
            icon: IconClipboardText,
        },
        {
            name: t('envelope_method'),
            text: t('sw_page.allocate_cash_to_categories'),
            note: t('sw_page.visual_spending_limits'),
            icon: IconBox,
        },
        {
            name: t('sw_page.simple_tracking'),
            text: t('sw_page.just_track_inc_and_exp'),
            note: t('sw_page.minimal_structure'),
            icon: IconNotes,
        },
    ];

    const EXPENSES = [
        {
            icon: IconHome,
            name: "Home Maintenance & Repairs", // Keep static
            label: t("sw_page.expense_home_maintenance_label"),
        },
        {
            icon: IconCoffee,
            name: "Groceries",
            label: t("sw_page.expense_groceries_label"),
        },
        {
            icon: IconWheel,
            name: "Public Transport",
            label: t("sw_page.expense_public_transport_label"),
        },
        {
            icon: IconDollarSignCircle,
            name: "Miscellaneous Shopping",
            label: t("sw_page.expense_misc_shopping_label"),
        },
        {
            icon: IconVideo,
            name: "Movies, Concerts & Events",
            label: t("sw_page.expense_entertainment_label"),
        },
        {
            icon: IconTag,
            name: "Other Expenses",
            label: t("sw_page.expense_other_label"),
        },
    ];

    return {
        STEPS,
        GOALS,
        METHODS,
        EXPENSES,
    };
}

export default SetupWizard;
